import React, { Fragment, useEffect } from "react";
import BulkActionsBar from "./ZBulkActionsBar";
import FilterSettingsBar from "./ZFilterSettingsBar";
import ZTable from "@organisms/ZTable";
import TableRecordsSelection from "./ZTableRecordsSelection";
import { ITableWithDecoratorsProps } from "./interface";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import "./index.less";
import useMobileView from "@/Hooks/useMobileView";
import { useHistory, useLocation } from "react-router-dom";
import { TablePaginationConfig } from "antd";
import {
	FilterValue,
	SorterResult,
	TableCurrentDataSource
} from "antd/lib/table/interface";

const ZTableWithDecorators = <T extends Record<string, any>>({
	isBulkActionsRecordsSelected = false,
	selectedRecords = 0,
	totalRecords = 0,
	selectAllRecords = false,
	filters = [],
	settingsColumns = [],
	activeFilters = [],
	isLoading = true,
	dataSource = [],
	columns = [],
	selectedRowKeys = [],
	isFilterNeeded = true,
	allowScroll = true,
	shouldShowDisplayRecords = true,
	showInfoText = true,
	leftComponents,
	rightComponents,
	allowYScroll = true,
	...rest
}: ITableWithDecoratorsProps<T>): React.ReactElement => {
	const { appUpdateBannerVisible, isBannerOpen } = useSelector(
		(state: GlobalState) => ({
			appUpdateBannerVisible: state.settings.appUpdateBannerVisible,
			isBannerOpen:
				state.bannerNotification.isOpen ||
				state.bannerNotification.isPaymentDueBannerOpen
		})
	);

	const isMobileView = useMobileView();
	const history = useHistory();

	const location = useLocation();

	const query = new URLSearchParams(location.search);

	const tableOnChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<T> | SorterResult<T>[],
		extra: TableCurrentDataSource<T>
	) => {
		rest?.onChange?.(pagination, filters, sorter, extra);
		history.push({
			search: `?page=${pagination.current}&page_size=${pagination.pageSize}`
		});
	};

	const updateDisplayRecords = (displayRecords: number) => {
		rest.updateSelectedRecords?.(displayRecords);
		history.push({
			search: `?page=1&page_size=${displayRecords}`
		});
	};

	useEffect(() => {
		if (query.get("page_size")) {
			rest.updateSelectedRecords?.(Number(query.get("page_size") ?? 25));
		}
		if (query.get("page")) {
			rest.onChange?.(
				{
					current: Number(query.get("page") ?? 1)
				},
				{},
				[],
				{} as TableCurrentDataSource<any>
			);
		}
	}, []);

	let tableHeight = !selectedRecords ? 19 : 22.2;
	if (appUpdateBannerVisible) tableHeight += 2.3;
	if (isBannerOpen) tableHeight += 2;
	if (isMobileView) tableHeight -= isBulkActionsRecordsSelected ? -0.5 : 3;
	return (
		<Fragment>
			{isBulkActionsRecordsSelected ? (
				<BulkActionsBar
					entity={rest.entity}
					selectedRecords={selectedRecords}
					totalRecords={totalRecords}
					handleRecordSelection={rest.handleRecordSelection}
					handleDelete={rest.handleDelete ?? undefined}
					openBulkUpdateDrawer={rest.openBulkUpdateDrawer}
					selectAllRecords={selectAllRecords}
					featureAccess={rest.featureAccess}
					showInfoText={showInfoText}
				/>
			) : (
				isFilterNeeded && (
					<FilterSettingsBar
						filters={filters}
						settingsColumns={settingsColumns}
						onFilterSubmit={rest.onFilterSubmit}
						onColumnsSubmit={rest.onColumnsSubmit}
						activeFilters={activeFilters}
						leftComponents={leftComponents}
						rightComponents={rightComponents}
						showArchivedRecords={rest.showArchivedRecords}
						onShowArchivedRecordsChange={
							rest.onShowArchivedRecordsChange
						}
						showArchivedCheckbox={rest.showArchivedCheckbox}
					/>
				)
			)}
			<div
				className={`z-table-container ${
					isMobileView ? "mobile-view" : ""
				} ${
					isMobileView && !isBulkActionsRecordsSelected ? "mt-0" : ""
				}`}
			>
				{!rest.isDraggableRow ? (
					<ZTable
						className={`z-table-with-decorators ${
							isBannerOpen ? "banner-open" : ""
						}`}
						loading={isLoading}
						scroll={
							allowScroll
								? {
										x: "calc(100vw - 120px)",
										y: `calc(var(--vh, 1vh) * 100 - ${tableHeight}rem)`
								  }
								: undefined
						}
						dataSource={dataSource}
						rowKey="id"
						pagination={
							rest.currentPagination instanceof Object
								? {
										...rest.currentPagination,
										pageSize: rest.displayRecords,
										showSizeChanger: isMobileView,
										simple: isMobileView
								  }
								: false
						}
						columns={columns}
						onChange={tableOnChange}
						rowSelection={
							rest.handleTableRecordSelection
								? {
										onChange:
											rest.handleTableRecordSelection,
										selectedRowKeys
								  }
								: undefined
						}
						rowClassName={rest.rowClassName}
					/>
				) : (
					<ZTable
						className={`z-table-with-decorators ${
							isBannerOpen ? "banner-open" : ""
						}`}
						onDragEnd={rest.onDragEnd}
						columns={columns}
						isDraggable={rest.isDraggable}
						dataSource={dataSource}
						pagination={false}
						loading={isLoading}
						scroll={
							allowScroll
								? {
										x: "calc(100vw - 120px)",
										y: allowYScroll
											? `calc(var(--vh, 1vh) * 100 - ${tableHeight}rem)`
											: undefined
								  }
								: undefined
						}
						isDragDisabled={rest?.isDragDisabled}
						rowClassName={rest.rowClassName}
					/>
				)}
				{shouldShowDisplayRecords && !rest.isDraggableRow && (
					<div className="records-selection">
						<TableRecordsSelection
							displayRecords={rest.displayRecords}
							updateSelectedRecords={updateDisplayRecords}
						/>
					</div>
				)}
			</div>
		</Fragment>
	);
};

export default ZTableWithDecorators;
